import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const ALL_FEATURES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'whatsapp',  label: 'WhatsApp & Bot' },
  { key: 'chats',     label: 'Live Chats' },
  { key: 'leads',     label: 'Leads CRM' },
];
const DEFAULT_FEATURES = ['dashboard', 'whatsapp', 'chats', 'leads'];

const ALL_APPS = [
  { key: 'bot',           label: '🤖 Bot Admin',      desc: 'WhatsApp AI bot, chats, leads' },
  { key: 'broadcaster',   label: '📢 Broadcaster',     desc: 'Bulk messaging & campaigns' },
  { key: 'support_agent', label: '🎫 Support Agent',   desc: 'Ticket-based learner support with DB lookup' },
];

function AppCheckboxes({ selected, onChange }) {
  function toggle(key) {
    if (selected.includes(key)) onChange(selected.filter(a => a !== key));
    else onChange([...selected, key]);
  }
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
      {ALL_APPS.map(a => {
        const checked = selected.includes(a.key);
        return (
          <label key={a.key} style={{
            display: 'flex', flexDirection: 'column', cursor: 'pointer',
            background: checked ? '#1a2e1a' : '#0f172a',
            border: `1px solid ${checked ? '#22c55e' : '#334155'}`,
            borderRadius: 8, padding: '8px 14px', minWidth: 150,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(a.key)} />
              <span style={{ color: checked ? '#86efac' : '#94a3b8', fontWeight: 700, fontSize: 13 }}>{a.label}</span>
            </div>
            <span style={{ fontSize: 11, color: '#475569', marginTop: 3, marginLeft: 20 }}>{a.desc}</span>
          </label>
        );
      })}
    </div>
  );
}

function FeatureCheckboxes({ selected, onChange, label = 'Features' }) {
  function toggle(key) {
    if (selected.includes(key)) onChange(selected.filter(f => f !== key));
    else onChange([...selected, key]);
  }
  return (
    <div style={{ marginBottom: 16 }}>
      <span style={s.label}>{label}</span>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
        {ALL_FEATURES.map(f => (
          <label key={f.key} style={s.checkLabel}>
            <input type="checkbox" checked={selected.includes(f.key)} onChange={() => toggle(f.key)} style={{ marginRight: 5 }} />
            {f.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function OrgUsersDrawer({ org, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addAdminForm, setAddAdminForm] = useState({ name: '', email: '', password: '' });
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/api/org/users', { params: { organizationId: org._id } })
      .then(({ data }) => setUsers(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [org._id]);

  async function createOrgAdmin(e) {
    e.preventDefault();
    try {
      const { data } = await api.post('/api/org/users', { ...addAdminForm, role: 'orgadmin', organizationId: org._id });
      setUsers(prev => [...prev, data]);
      setAddAdminForm({ name: '', email: '', password: '' });
      setShowAddAdmin(false);
      setMsg('Org admin created!');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create admin');
    }
  }

  async function toggleActive(u) {
    try {
      const { data } = await api.patch(`/api/org/users/${u._id}`, { isActive: !u.isActive });
      setUsers(prev => prev.map(x => x._id === u._id ? data : x));
    } catch {}
  }

  return (
    <div style={s.drawer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: 18 }}>{org.name}</h3>
          <span style={{ color: '#64748b', fontSize: 13 }}>
            {(org.allowedApps || ['bot']).includes('bot') && `🤖 Bot: ${org.botUserCount ?? '?'} / ${org.botLicenseCount ?? org.licenseCount ?? '?'}`}
            {(org.allowedApps || []).includes('broadcaster') && `  📢 BC: ${org.broadcasterUserCount ?? 0} / ${org.broadcasterLicenseCount ?? 0}`}
          </span>
        </div>
        <button onClick={onClose} style={s.btnGhost}>✕ Close</button>
      </div>

      {msg && <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 12 }}>{msg}</p>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ color: '#94a3b8', fontSize: 14 }}>Users in this org</span>
        <button onClick={() => setShowAddAdmin(!showAddAdmin)} style={s.btnSm}>
          {showAddAdmin ? 'Cancel' : '+ Add Org Admin'}
        </button>
      </div>

      {showAddAdmin && (
        <form onSubmit={createOrgAdmin} style={{ ...s.formRow, marginBottom: 16, background: '#0f172a', padding: 16, borderRadius: 8 }}>
          <input placeholder="Full name" required style={s.input} value={addAdminForm.name} onChange={e => setAddAdminForm({ ...addAdminForm, name: e.target.value })} />
          <input placeholder="Email" type="email" required style={s.input} value={addAdminForm.email} onChange={e => setAddAdminForm({ ...addAdminForm, email: e.target.value })} />
          <input placeholder="Password" type="password" required style={s.input} value={addAdminForm.password} onChange={e => setAddAdminForm({ ...addAdminForm, password: e.target.value })} />
          <button type="submit" style={s.btnPrimary}>Create Org Admin</button>
        </form>
      )}

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading...</p>
      ) : users.length === 0 ? (
        <p style={{ color: '#64748b', fontSize: 14 }}>No users yet. Add an Org Admin above to get started.</p>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>{['Name', 'Email', 'Role', 'Status', ''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u._id}>
                <td style={s.td}>{u.name}</td>
                <td style={s.td}>{u.email}</td>
                <td style={s.td}>
                  <span style={{ ...s.badge, background: u.role === 'orgadmin' ? '#1e3a5f' : '#1e293b', color: u.role === 'orgadmin' ? '#60a5fa' : '#94a3b8' }}>{u.role}</span>
                </td>
                <td style={s.td}>
                  <span style={{ ...s.badge, background: u.isActive ? '#166534' : '#7f1d1d', color: u.isActive ? '#86efac' : '#fca5a5' }}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={s.td}>
                  <button
                    style={{ ...s.btnSm, background: u.isActive ? '#7f1d1d' : '#166534', color: u.isActive ? '#fca5a5' : '#86efac', fontSize: 11 }}
                    onClick={() => toggleActive(u)}
                  >
                    {u.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [createForm, setCreateForm] = useState({ name: '', botLicenseCount: 5, broadcasterLicenseCount: 0, allowedApps: ['bot'], allowedFeatures: [...DEFAULT_FEATURES] });
  const [showCreate, setShowCreate] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => { fetchOrgs(); }, []);

  async function fetchOrgs() {
    try {
      setLoading(true);
      const { data } = await api.get('/api/org/organizations');
      setOrgs(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      const { data } = await api.post('/api/org/organizations', {
        name: createForm.name,
        botLicenseCount: Number(createForm.botLicenseCount),
        broadcasterLicenseCount: Number(createForm.broadcasterLicenseCount),
        allowedApps: createForm.allowedApps,
        allowedFeatures: createForm.allowedFeatures,
      });
      setOrgs(prev => [data, ...prev]);
      setCreateForm({ name: '', botLicenseCount: 5, broadcasterLicenseCount: 0, allowedApps: ['bot'], allowedFeatures: [...DEFAULT_FEATURES] });
      setShowCreate(false);
      flash('Organization created!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create organization');
    }
  }

  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  function startEdit(org) {
    setEditingOrgId(org._id);
    setEditForm({
      name: org.name,
      botLicenseCount: org.botLicenseCount ?? org.licenseCount ?? 1,
      broadcasterLicenseCount: org.broadcasterLicenseCount ?? 0,
      isActive: org.isActive,
      allowedApps: org.allowedApps || ['bot'],
      allowedFeatures: org.allowedFeatures || [],
      logo: org.logo || null,
    });
  }

  async function handleUpdate() {
    try {
      const { data } = await api.put(`/api/org/organizations/${editingOrgId}`, {
        name: editForm.name,
        botLicenseCount: Number(editForm.botLicenseCount),
        broadcasterLicenseCount: Number(editForm.broadcasterLicenseCount),
        isActive: editForm.isActive,
        allowedApps: editForm.allowedApps,
        allowedFeatures: editForm.allowedFeatures,
      });
      setOrgs(prev => prev.map(o => o._id === editingOrgId ? { ...data, logo: editForm.logo } : o));
      setEditingOrgId(null);
      flash('Organization updated!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file || !editingOrgId) return;
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const { data } = await api.put(`/api/org/organizations/${editingOrgId}/logo`, formData);
      setEditForm(prev => ({ ...prev, logo: data.logo }));
      setOrgs(prev => prev.map(o => o._id === editingOrgId ? { ...o, logo: data.logo } : o));
      flash('Logo updated!');
    } catch (err) {
      alert(err.response?.data?.error || 'Logo upload failed');
    }
    setLogoUploading(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  }

  async function removeLogo(orgId) {
    try {
      await api.delete(`/api/org/organizations/${orgId}/logo`);
      setEditForm(prev => ({ ...prev, logo: null }));
      setOrgs(prev => prev.map(o => o._id === orgId ? { ...o, logo: null } : o));
    } catch {}
  }

  function flash(msg) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); }

  return (
    <div style={s.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={s.title}>Organizations</h1>
          <p style={s.desc}>Manage client organizations, their license quotas, and enabled features.</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} style={s.btnPrimary}>
          {showCreate ? 'Cancel' : '+ New Organization'}
        </button>
      </div>

      {successMsg && <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 16 }}>{successMsg}</p>}
      {error && <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>}

      {showCreate && (
        <div style={{ ...s.card, border: '1px solid #6366f1' }}>
          <h3 style={s.cardTitle}>Create New Organization</h3>
          <form onSubmit={handleCreate}>
            <div style={s.formRow}>
              <div style={{ flex: 2 }}>
                <label style={s.label}>Organization Name</label>
                <input type="text" placeholder="e.g. Acme Corp" required style={{ ...s.input, width: '100%' }}
                  value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Bot Licenses</label>
                <input type="number" min={0} style={{ ...s.input, width: '100%' }}
                  value={createForm.botLicenseCount} onChange={e => setCreateForm({ ...createForm, botLicenseCount: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Broadcaster Licenses</label>
                <input type="number" min={0} style={{ ...s.input, width: '100%' }}
                  value={createForm.broadcasterLicenseCount} onChange={e => setCreateForm({ ...createForm, broadcasterLicenseCount: e.target.value })} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <span style={s.label}>Apps to Enable</span>
              <AppCheckboxes selected={createForm.allowedApps} onChange={a => setCreateForm({ ...createForm, allowedApps: a })} />
            </div>
            {createForm.allowedApps.includes('bot') && (
              <FeatureCheckboxes label="Bot Sub-Features" selected={createForm.allowedFeatures} onChange={f => setCreateForm({ ...createForm, allowedFeatures: f })} />
            )}
            <button type="submit" style={s.btnPrimary}>Create Organization</button>
          </form>
        </div>
      )}

      <div style={s.card}>
        <h3 style={s.cardTitle}>All Organizations ({orgs.length})</h3>
        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : orgs.length === 0 ? (
          <p style={{ color: '#64748b' }}>No organizations yet.</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>{['Name', 'Apps', 'Bot Licenses', 'BC Licenses', 'Bot Features', 'Status', 'Created', 'Actions'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {orgs.map(org => (
                <React.Fragment key={org._id}>
                  {editingOrgId === org._id ? (
                    <tr>
                      <td style={s.td} colSpan={6}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, background: '#0f172a', padding: 16, borderRadius: 8 }}>
                          <div style={s.formRow}>
                            <div style={{ flex: 2 }}>
                              <label style={s.label}>Name</label>
                              <input style={s.input} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={s.label}>Bot Licenses</label>
                              <input type="number" min={0} style={s.input} value={editForm.botLicenseCount} onChange={e => setEditForm({ ...editForm, botLicenseCount: e.target.value })} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={s.label}>Broadcaster Licenses</label>
                              <input type="number" min={0} style={s.input} value={editForm.broadcasterLicenseCount} onChange={e => setEditForm({ ...editForm, broadcasterLicenseCount: e.target.value })} />
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <label style={s.label}>Status</label>
                              <label style={s.checkLabel}>
                                <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })} style={{ marginRight: 6 }} />
                                Active
                              </label>
                            </div>
                          </div>
                          <div style={{ marginBottom: 14 }}>
                            <span style={s.label}>Apps Enabled</span>
                            <AppCheckboxes selected={editForm.allowedApps || []} onChange={a => setEditForm({ ...editForm, allowedApps: a })} />
                          </div>
                          {/* Logo upload */}
                          <div>
                            <label style={s.label}>Organization Logo</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              {editForm.logo
                                ? <img src={editForm.logo} alt="logo" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'contain', background: '#1e293b', border: '1px solid #334155', padding: 4 }} />
                                : <div style={{ width: 56, height: 56, borderRadius: 8, background: '#1e293b', border: '1px dashed #475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🏢</div>
                              }
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                                <button style={s.btnSm} onClick={() => logoInputRef.current.click()} disabled={logoUploading}>
                                  {logoUploading ? 'Uploading...' : editForm.logo ? 'Change Logo' : 'Upload Logo'}
                                </button>
                                {editForm.logo && <button style={{ ...s.btnSm, color: '#ef4444' }} onClick={() => removeLogo(editingOrgId)}>Remove</button>}
                                <span style={{ fontSize: 11, color: '#64748b' }}>PNG, JPG, SVG · max 2MB</span>
                              </div>
                            </div>
                          </div>
                          <FeatureCheckboxes label="Enabled Features" selected={editForm.allowedFeatures || []} onChange={f => setEditForm({ ...editForm, allowedFeatures: f })} />
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button style={s.btnPrimary} onClick={handleUpdate}>Save Changes</button>
                            <button style={s.btnGhost} onClick={() => setEditingOrgId(null)}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {org.logo
                            ? <img src={org.logo} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain', background: '#fff', padding: 2, border: '1px solid #334155' }} />
                            : <div style={{ width: 28, height: 28, borderRadius: 6, background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏢</div>
                          }
                          <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{org.name}</span>
                        </div>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(org.allowedApps || ['bot']).map(a => (
                            <span key={a} style={{ ...s.featurePill, background: a === 'broadcaster' ? '#2d1a4a' : '#1e3a5f', color: a === 'broadcaster' ? '#c084fc' : '#60a5fa' }}>
                              {a === 'bot' ? '🤖 Bot' : '📢 Broadcaster'}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{ color: (org.botUserCount ?? org.userCount) >= (org.botLicenseCount ?? org.licenseCount) ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{org.botUserCount ?? org.userCount}</span>
                        <span style={{ color: '#64748b' }}> / {org.botLicenseCount ?? org.licenseCount}</span>
                      </td>
                      <td style={s.td}>
                        {(org.allowedApps || []).includes('broadcaster') ? (
                          <>
                            <span style={{ color: (org.broadcasterUserCount ?? 0) >= (org.broadcasterLicenseCount ?? 0) ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{org.broadcasterUserCount ?? 0}</span>
                            <span style={{ color: '#64748b' }}> / {org.broadcasterLicenseCount ?? 0}</span>
                          </>
                        ) : <span style={{ color: '#334155' }}>—</span>}
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {(org.allowedFeatures || []).map(f => (
                            <span key={f} style={s.featurePill}>{FEATURE_LABELS[f] || f}</span>
                          ))}
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, background: org.isActive ? '#166534' : '#7f1d1d', color: org.isActive ? '#86efac' : '#fca5a5' }}>
                          {org.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={s.td}>{new Date(org.createdAt).toLocaleDateString()}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button style={s.btnSm} onClick={() => startEdit(org)}>Edit</button>
                          <button style={{ ...s.btnSm, background: '#1e3a5f', color: '#60a5fa' }} onClick={() => setSelectedOrg(selectedOrg?._id === org._id ? null : org)}>
                            {selectedOrg?._id === org._id ? 'Hide Users' : 'Manage Users'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {selectedOrg?._id === org._id && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <OrgUsersDrawer org={org} onClose={() => setSelectedOrg(null)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const FEATURE_LABELS = { dashboard: 'Dashboard', whatsapp: 'WhatsApp', chats: 'Chats', leads: 'Leads' };

const s = {
  page: { padding: '32px 40px', flex: 1, background: '#0f172a', color: '#f1f5f9', overflowY: 'auto' },
  title: { fontSize: 28, fontWeight: 700, margin: '0 0 6px' },
  desc: { color: '#64748b', fontSize: 14, margin: 0 },
  card: { background: '#1e293b', padding: 24, borderRadius: 12, border: '1px solid #334155', marginBottom: 24 },
  cardTitle: { fontSize: 16, fontWeight: 600, marginBottom: 20, color: '#e2e8f0' },
  formRow: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 6 },
  checkLabel: { display: 'flex', alignItems: 'center', fontSize: 13, color: '#cbd5e1', cursor: 'pointer', background: '#0f172a', padding: '5px 10px', borderRadius: 6, border: '1px solid #334155' },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#fff', fontSize: 14, outline: 'none' },
  btnPrimary: { padding: '9px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnSm: { padding: '5px 12px', borderRadius: 6, border: 'none', background: '#334155', color: '#e2e8f0', cursor: 'pointer', fontWeight: 600, fontSize: 12 },
  btnGhost: { padding: '5px 12px', borderRadius: 6, border: '1px solid #475569', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontSize: 13, borderBottom: '1px solid #334155' },
  td: { padding: '14px 12px', borderBottom: '1px solid #1e293b', color: '#cbd5e1', fontSize: 14 },
  badge: { padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 },
  featurePill: { background: '#1e3a5f', color: '#60a5fa', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
  drawer: { background: '#0d1829', border: '1px solid #1e3a5f', borderRadius: 8, padding: 20, margin: '0 0 8px' },
};
